import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { Typography, Button, Alert, CircularProgress } from "@ann/web-components";
import { useStore } from "../../../../../hooks";
import { ToolsPageHeader, ToolsPageContent, CircularProgressContainer } from "./ToolsPage.styled";
import DefaultTheme from "../../../../../theme/DefaultTheme";
import { SlideoutTools } from "./components/SlideoutTools";
import SelectedTools from "./components/SelectedTools";
import ToolConfigurationSlideout from "./components/ToolConfigurationSlideout";
import { useToolConfiguration } from "./hooks/useToolConfiguration";

const ToolsPage = observer(() => {
  const { agentId } = useParams();
  const { toolsStore } = useStore();
  const [showSlideout, setShowSlideout] = useState(false);

  const {
    showConfigurationSlideout,
    selectedToolForConfiguration,
    isFromToolsLibrary,
    openConfiguration,
    closeConfiguration,
    goBackToToolsLibrary,
    saveConfiguration,
  } = useToolConfiguration();

  useEffect(() => {
    if (agentId && toolsStore.currentAgentUuid !== agentId) {
      toolsStore.fetchAgentTools(agentId);
    }
  }, [agentId, toolsStore]);

  useEffect(() => {
    if (showSlideout && toolsStore.availableTools.length === 0) {
      toolsStore.fetchToolsList();
    }
  }, [showSlideout, toolsStore]);

  const handleToggleTool = useCallback(async (toolId) => {
    try {
      await toolsStore.toggleTool(agentId, toolId);
    } catch (error) {
    }
  }, [agentId, toolsStore]);

  const handleDelete = useCallback(async (toolId) => {
    try {
      await toolsStore.removeTool(agentId, toolId);
    } catch (error) {
    }
  }, [agentId, toolsStore]);

  const handleSettings = useCallback((tool) => {
    openConfiguration(tool);
  }, [openConfiguration]);

  const handleToolConfiguration = useCallback((tool) => {
    openConfiguration(tool, true);
  }, [openConfiguration]);

  const handleSaveConfiguration = useCallback(async (configurationData) => {
    try {
      await toolsStore.updateToolSettings(
        agentId,
        selectedToolForConfiguration.id,
        configurationData
      );
      await saveConfiguration(configurationData);
      closeConfiguration();
    } catch (error) {
    }
  }, [agentId, toolsStore, selectedToolForConfiguration, saveConfiguration, closeConfiguration]);

  const handleAddTools = useCallback(async (selectedToolObjects) => {
    try {
      if (!selectedToolObjects?.length) throw new Error("No tools selected");
      
      await toolsStore.addTools(agentId, selectedToolObjects);
      setShowSlideout(false);
    } catch (error) {
    }
  }, [agentId, toolsStore]);

  if (toolsStore.isLoadingAgentTools && toolsStore.installedTools.length === 0) {
    return (
      <CircularProgressContainer>
        <CircularProgress size="xl" />
      </CircularProgressContainer>
    );
  }

  return (
    <>
      <ToolsPageContent>
        <ToolsPageHeader davysGrey={DefaultTheme.colors.davysGrey}>
          <div>
            <Typography category="subtitle" size="medium" weight="bold">
              Tool Library
            </Typography>
            <Typography category="body" size="medium" weight="regular">
              Give the agent extra abilities to act, respond, or automate.
            </Typography>
          </div>
          <Button
            iconLeft="plus"
            label="Add"
            size="medium"
            type="secondary"
            onClick={() => setShowSlideout(true)}
          />
        </ToolsPageHeader>

        <SelectedTools
          installedTools={toolsStore.installedTools}
          onToggleTool={handleToggleTool}
          onSettings={handleSettings}
          onDelete={handleDelete}
        />
      </ToolsPageContent>

      <SlideoutTools
        open={showSlideout}
        onClose={() => setShowSlideout(false)}
        onAddTools={handleAddTools}
        onToolConfiguration={handleToolConfiguration}
        installedTools={toolsStore.installedTools}
        availableTools={toolsStore.availableTools}
        isLoading={toolsStore.isLoadingToolsList}
      />

      <ToolConfigurationSlideout
        open={showConfigurationSlideout}
        onClose={closeConfiguration}
        tool={selectedToolForConfiguration}
        onSave={handleSaveConfiguration}
        showBackButton={isFromToolsLibrary}
        onBack={goBackToToolsLibrary}
      />

      {toolsStore.showAlert && (
        <Alert
          severity={toolsStore.alertType}
          title={toolsStore.alertMessage}
          onClose={() => toolsStore.hideNotification()}
          maxWidth="500px"
          closable={true}
          autoClose={true}
          autoCloseDelay={5000}
        />
      )}

      {toolsStore.agentToolsError && (
        <Alert
          severity="error"
          title={toolsStore.agentToolsError}
          onClose={() => toolsStore.clearErrors()}
        />
      )}
    </>
  );
});

export default ToolsPage;
